import { Alert, Button, Checkbox, Modal, Tooltip } from "@arco-design/web-react";
import {
  ArrowDown,
  ArrowRight,
  ArrowUp,
  GripVertical,
  MapPinPlus,
  Pencil,
  Plus,
  Search,
  Trash2,
  TriangleAlert,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";

import { createUuid } from "../../../lib/trace/context";
import { insertSelectedRailway, type RailwayInsertionDraft } from "../service/itineraryRailwayInsertionService";
import { railwayDaySegments } from "../service/itineraryRailwayOvernightService";
import { resolveRailwayTransferContext } from "../service/itineraryRailwayTransferService";
import { resolvePointRecommendationAnchor } from "../service/itineraryCandidateRecommendationService";
import {
  addNode,
  applyTransportWorkspaceChange,
  captureTransportWorkspaceBaseline,
  completeDayStayEndpoints,
  countReplacedFilledTransportSegments,
  countTransportWorkspaceFactLoss,
  deleteNode,
  insertNodeAfter,
  insertNodeBefore,
  isStayPointConnection,
  itineraryEditorSemanticFingerprint,
  movePoint,
  getProfessionalColor,
  getProfessionalNodeMajorId,
  getItineraryProfessionalSettings,
  hasExplicitProfessionalRoutes,
  formatSegmentRouteSummary,
  transportPlacementNotice,
  transportPlacementError,
  requiresRouteConfirmation,
  setItineraryStartDate,
  updateNode,
  validateEditor,
} from "../service";
import type {
  DrivingRequest,
  DrivingResult,
  FlightQuery,
  FlightSearchResult,
  ItineraryDay,
  ItineraryEditor,
  ItineraryNode,
  ItinerarySegment,
  MajorCandidate,
  MajorCandidateQuery,
  NodeDraft,
  PointCandidateResult,
  PointCandidateQuery,
  RailwayQuery,
  RailwaySearchResult,
  RailwayTransferPlan,
  RailwayTransferContext,
  TimeSlot,
  TransportEndpointChange,
  TransportWorkspaceBaseline,
  TransportWorkspaceChange,
  WalkingRequest,
  WalkingResult,
} from "../types";
import { ItineraryDialogCloseButton } from "./ItineraryDialogCloseButton";
import { ItineraryFlowCanvas } from "./ItineraryFlowCanvas";
import { ItineraryPointDialog, type PointNodeCandidateKind } from "./ItineraryPointDialog";
import { ItineraryRouteSummaryCard } from "./ItineraryRouteSummaryCard";
import { ItineraryProfessionalSettings } from "./ItineraryProfessionalSettings";
import { ItineraryTransportOptionsDrawer } from "./ItineraryTransportOptionsDrawer";

type Props = {
  editor: ItineraryEditor;
  day: ItineraryDay;
  onChange: (editor: ItineraryEditor) => void;
  onAnnounce: (message: string) => void;
  listPointCandidates?: (query: PointCandidateQuery, signal?: AbortSignal) => Promise<PointCandidateResult>;
  listMajorCandidates?: (query?: MajorCandidateQuery) => Promise<MajorCandidate[]>;
  getRailwayOptions?: (query: RailwayQuery, signal?: AbortSignal) => Promise<RailwaySearchResult>;
  getFlightOptions?: (query: FlightQuery, signal?: AbortSignal) => Promise<FlightSearchResult>;
  getDrivingRoute?: (request: DrivingRequest, signal?: AbortSignal) => Promise<DrivingResult>;
  getWalkingRoute?: (request: WalkingRequest, signal?: AbortSignal) => Promise<WalkingResult>;
  onModalDraftChange?: (dirty: boolean) => void;
  calculatingSegmentIds?: ReadonlySet<string>;
};

type PointDialogState =
  | { mode: "add"; slot: TimeSlot }
  | { mode: "before" | "after" | "edit"; slot: TimeSlot; node: ItineraryNode };

type DragTarget =
  | { kind: "node"; nodeId: string }
  | { kind: "slot"; timeSlot: TimeSlot }
  | null;

const slotLabels: Record<TimeSlot, string> = { morning: "上午", afternoon: "下午", evening: "晚上" };
const nodeTypeLabels: Record<ItineraryNode["nodeType"], string> = {
  point: "考察点",
  railway_station: "火车站",
  airport: "机场",
  stay_area: "住宿点",
  organization_site: "学校校区",
  custom: "考察点",
};

const createId = createUuid;
const pointNodeCandidateKinds: readonly PointNodeCandidateKind[] = ["point", "stay_area", "railway_station", "airport", "organization_site"];
const nodePositionMessage = "学校校区只能放在当天首尾";
const timeSlotOrder: readonly TimeSlot[] = ["morning", "afternoon", "evening"];
const endpointNodeTypes = new Set<ItineraryNode["nodeType"]>(["stay_area", "organization_site"]);

/** 只有显示方式而没有结构化交通事实时，住宿跨日衔接直接绘制连接线。 */
function hasSavedTransportFacts(segment: ItinerarySegment): boolean {
  return Boolean(segment.transportSpuId || segment.transportSkuId || segment.busSpuId || segment.busSkuId
    || segment.routeCode || segment.departureAt || segment.arrivalAt || segment.distanceKm
    || segment.durationMinutes !== undefined && segment.durationMinutes !== null
    || segment.segmentDescription?.trim() || segment.remark?.trim()
    || segment.sourcePayload?.selected || segment.sourcePayload?.backups.length
    || segment.sourcePayload?.toolResultSnapshot);
}

/** 返回本次跨时段移动后按目标端点时段重查的已有铁路段。 */
function railwaySegmentsToRequeryAcrossSlot(
  editor: ItineraryEditor,
  node: ItineraryNode,
  targetSlot: TimeSlot,
): ReadonlySet<string> {
  if (node.timeSlot === targetSlot) return new Set();
  return new Set(editor.segments.filter((segment) => (
    segment.transportMode === "rail"
    && (segment.fromNodeId === node.id || segment.toNodeId === node.id)
    && hasSavedTransportFacts(segment)
  )).map((segment) => segment.id));
}

/** 从历史轨道和专业路线中提取仅用于画布识别的专业名称，不写回行程设置。 */
function collectDisplayedProfessionalOptions(editor: ItineraryEditor): Array<{ id: string; name: string }> {
  const options = new Map<string, { id: string; name: string }>();
  editor.days.flatMap((day) => day.parallelTracks?.tracks ?? []).forEach((track) => {
    if (!track.majorId || options.has(track.majorId)) return;
    options.set(track.majorId, { id: track.majorId, name: track.title?.trim() || `专业 ${options.size + 1}` });
  });
  editor.segments.forEach((segment) => {
    if (!segment.majorId || options.has(segment.majorId)) return;
    options.set(segment.majorId, { id: segment.majorId, name: `专业 ${options.size + 1}` });
  });
  return [...options.values()];
}

/** 将一组显式路线段按真实端点拼成摘要，不能退回全日节点数组猜测路线。 */
function describeSegmentPaths(
  segments: ItinerarySegment[],
  nodeLocations: Map<string, { node: ItineraryNode }>,
): string {
  const remaining = new Map(segments.map((segment) => [segment.id, segment]));
  const paths: string[][] = [];
  while (remaining.size) {
    const values = [...remaining.values()].sort((left, right) => left.routeSeq - right.routeSeq || left.id.localeCompare(right.id));
    const incoming = new Set(values.map((segment) => segment.toNodeId));
    const start = values.find((segment) => !incoming.has(segment.fromNodeId)) ?? values[0];
    const path = [start.fromNodeId];
    let current = start.fromNodeId;
    while (true) {
      const next = [...remaining.values()]
        .filter((segment) => segment.fromNodeId === current)
        .sort((left, right) => left.routeSeq - right.routeSeq || left.id.localeCompare(right.id))[0];
      if (!next) break;
      remaining.delete(next.id);
      path.push(next.toNodeId);
      current = next.toNodeId;
    }
    paths.push(path.map((nodeId) => nodeLocations.get(nodeId)?.node.title ?? "待补点位"));
  }
  return paths.map((path) => path.join(" → ")).join("；");
}

/** 返回追加点位时需要保持在末尾的住宿或学校端点。 */
function terminalNodeBeforeAppend(
  editor: ItineraryEditor,
  dayId: string,
  timeSlot: TimeSlot,
  movingNodeId?: string,
): ItineraryNode | null {
  const day = editor.days.find((item) => item.id === dayId);
  if (!day) return null;
  const orderedBeforeMove = timeSlotOrder.flatMap((slot) => day.nodes
    .filter((node) => node.timeSlot === slot)
    .sort((left, right) => left.sortOrder - right.sortOrder));
  const remaining = day.nodes.filter((node) => node.id !== movingNodeId);
  const ordered = timeSlotOrder.flatMap((slot) => remaining
    .filter((node) => node.timeSlot === slot)
    .sort((left, right) => left.sortOrder - right.sortOrder));
  const terminal = ordered.at(-1);
  if (
    orderedBeforeMove.length < 2
    || !terminal
    || orderedBeforeMove.at(-1)?.id !== terminal.id
    || terminal.timeSlot !== timeSlot
    || !endpointNodeTypes.has(terminal.nodeType)
  ) return null;
  return terminal;
}

/** 计算普通追加在目标时段中的安全索引。 */
function appendIndex(
  editor: ItineraryEditor,
  dayId: string,
  timeSlot: TimeSlot,
  movingNodeId?: string,
): number {
  const day = editor.days.find((item) => item.id === dayId);
  if (!day) return 0;
  const slotNodes = day.nodes
    .filter((node) => node.id !== movingNodeId && node.timeSlot === timeSlot)
    .sort((left, right) => left.sortOrder - right.sortOrder);
  const terminal = terminalNodeBeforeAppend(editor, dayId, timeSlot, movingNodeId);
  return terminal ? slotNodes.findIndex((node) => node.id === terminal.id) : slotNodes.length;
}

/** 复用真实编辑命令预演点位弹窗提交，避免位置规则在 UI 重写一份。 */
function applyPointDraft(
  editor: ItineraryEditor,
  dayId: string,
  target: PointDialogState,
  draft: NodeDraft & { timeSlot: TimeSlot },
  idFactory: () => string,
  majorId?: string | null,
): ItineraryEditor {
  if (target.mode === "add") {
    const terminal = terminalNodeBeforeAppend(editor, dayId, draft.timeSlot);
    return terminal
      ? insertNodeBefore(editor, terminal.id, draft, idFactory, { majorId })
      : addNode(editor, dayId, draft.timeSlot, draft, idFactory, { majorId });
  }
  if (target.mode === "before") return insertNodeBefore(editor, target.node.id, draft, idFactory, { majorId });
  if (target.mode === "after") return insertNodeAfter(editor, target.node.id, draft, idFactory, { majorId });
  const next = updateNode(editor, target.node.id, draft);
  const latestNode = next.days.flatMap((day) => day.nodes).find((node) => node.id === target.node.id)!;
  if (draft.timeSlot === target.node.timeSlot || draft.timeSlot === latestNode.timeSlot) return next;
  const targetIndex = appendIndex(next, dayId, draft.timeSlot, target.node.id);
  return movePoint(next, target.node.id, { dayId, timeSlot: draft.timeSlot, index: targetIndex }, idFactory, { majorId });
}

/** 构造只用于位置预演的最小结构化资源。 */
function candidatePreviewDraft(
  kind: PointNodeCandidateKind,
  timeSlot: TimeSlot,
): NodeDraft & { timeSlot: TimeSlot } {
  return {
    title: "位置预演点位",
    nodeType: kind,
    timeSlot,
    pointSpuId: ["point", "railway_station", "airport"].includes(kind) ? "preview-point" : null,
    stayAreaId: kind === "stay_area" ? "preview-stay" : null,
    organizationSiteId: kind === "organization_site" ? 1 : null,
  };
}

/** 提取稳定位置错误主体，供 UI 只阻止本次操作引入新违规。 */
function nodePositionIssueKeys(editor: ItineraryEditor): Set<string> {
  return new Set(validateEditor(editor)
    .filter((issue) => issue.code === "intermediate-node-must-be-point" && issue.subjectKey)
    .map((issue) => issue.subjectKey as string));
}

/** 判断 after 是否引入 before 中不存在的位置违规主体。 */
function hasNewNodePositionIssues(before: Set<string>, after: Set<string>): boolean {
  return [...after].some((subjectKey) => !before.has(subjectKey));
}

/** 行程骨架编辑区：点位顺序、时段和相邻路线。 */
export function ItinerarySkeletonPanel({
  editor,
  day,
  onChange,
  onAnnounce,
  listPointCandidates,
  listMajorCandidates,
  getRailwayOptions,
  getFlightOptions,
  getDrivingRoute,
  getWalkingRoute,
  onModalDraftChange,
  calculatingSegmentIds,
}: Props) {
  const [hideShortBusDetails, setHideShortBusDetails] = useState(() => {
    try {
      return globalThis.sessionStorage?.getItem("itinerary:hide-short-bus-details") !== "false";
    } catch {
      return true;
    }
  });
  const [pointDialog, setPointDialog] = useState<PointDialogState | null>(null);
  const [confirmDividedDayId, setConfirmDividedDayId] = useState<string | null>(null);
  useEffect(() => { setConfirmDividedDayId(null); }, [day.id]);
  const [transportSegment, setTransportSegment] = useState<ItinerarySegment | null>(null);
  const [transportBaseline, setTransportBaseline] = useState<TransportWorkspaceBaseline | null>(null);
  const [railwayTransferContext, setRailwayTransferContext] = useState<RailwayTransferContext | null>(null);
  const [pendingTransportWorkspace, setPendingTransportWorkspace] = useState<{
    change: TransportWorkspaceChange;
    count: number;
  } | null>(null);
  const [pendingDeleteNode, setPendingDeleteNode] = useState<ItineraryNode | null>(null);
  const [pendingTransportLoss, setPendingTransportLoss] = useState<{
    next: ItineraryEditor;
    baselineFingerprint: string;
    action: string;
    message: string;
    focusNodeId?: string;
    count: number;
    onApplied?: () => void;
  } | null>(null);
  const [draggedNodeId, setDraggedNodeId] = useState("");
  const [dragTarget, setDragTarget] = useState<DragTarget>(null);
  const [interactionError, setInteractionError] = useState<{
    dayId: string;
    message: string;
  } | null>(null);
  const pointerDragRef = useRef<{
    nodeId: string;
    pointerId: number;
    startX: number;
    startY: number;
    lastX: number;
    lastY: number;
    pointerType: string;
    nativeDragging: boolean;
  } | null>(null);
  const transportTriggerRef = useRef<HTMLElement | null>(null);
  const restoreTransportFocusRef = useRef(false);
  const deleteDialogTriggerRef = useRef<HTMLElement | null>(null);
  const restoreDeleteDialogFocusRef = useRef(false);
  const pointDialogTriggerRef = useRef<HTMLElement | null>(null);
  const restorePointDialogFocusRef = useRef(false);
  const hasParallelTracks = (day.parallelTracks?.tracks.length ?? 0) > 0;
  const professionalSettings = getItineraryProfessionalSettings(editor);
  const configuredProfessionalMajorOptions = professionalSettings?.majors ?? [];
  const displayedProfessionalMajorOptions = [
    ...configuredProfessionalMajorOptions,
    ...collectDisplayedProfessionalOptions(editor).filter((major) => (
      !configuredProfessionalMajorOptions.some((configured) => configured.id === major.id)
    )),
  ];
  const professionalMajorOptions = configuredProfessionalMajorOptions;
  const editableMainProfessionalMajorId = professionalSettings?.mainMajorId ?? null;
  const displayedMainProfessionalMajorId = editableMainProfessionalMajorId
    ?? displayedProfessionalMajorOptions[0]?.id
    ?? null;
  const colorMajorIds = displayedMainProfessionalMajorId
    ? [displayedMainProfessionalMajorId, ...displayedProfessionalMajorOptions.map((major) => major.id).filter((id) => id !== displayedMainProfessionalMajorId)]
    : [];
  const pointContextMajorId = pointDialog && pointDialog.mode !== "add"
    ? getProfessionalNodeMajorId(editor, day.id, pointDialog.node.id)
    : null;
  const pointDialogMajorId = pointContextMajorId ?? editableMainProfessionalMajorId;
  const majorName = (majorId: string | null | undefined) => {
    if (!majorId) return "公共";
    return displayedProfessionalMajorOptions.find((major) => major.id === majorId)?.name
      ?? `专业 ${Math.max(1, colorMajorIds.indexOf(majorId) + 1)}`;
  };

  const applyCommittedChange = (
    next: ItineraryEditor,
    message: string,
    focusNodeId?: string,
    onApplied?: () => void,
  ) => {
    onChange(next);
    setInteractionError(null);
    onAnnounce(message);
    onApplied?.();
    if (focusNodeId) {
      document.getElementById(`itinerary-node-handle-${focusNodeId}`)?.focus();
    }
  };

  const commit = (
    command: () => ItineraryEditor,
    message: string,
    focusNodeId?: string,
    options?: {
      confirmTransportLoss?: boolean;
      exemptTransportSegmentIds?: ReadonlySet<string>;
      transportLossAction?: string;
      onApplied?: () => void;
    },
  ): boolean => {
    try {
      const next = command();
      if (hasNewNodePositionIssues(nodePositionIssueKeys(editor), nodePositionIssueKeys(next))) {
        throw new Error(nodePositionMessage);
      }
      const replacedCount = options?.confirmTransportLoss
        ? countReplacedFilledTransportSegments(editor, next, {
          exemptSegmentIds: options?.exemptTransportSegmentIds,
        })
        : 0;
      if (replacedCount > 0) {
        setPendingTransportLoss({
          next,
          baselineFingerprint: itineraryEditorSemanticFingerprint(editor),
          action: options?.transportLossAction ?? "移动",
          message,
          focusNodeId,
          count: replacedCount,
          onApplied: options?.onApplied,
        });
        return false;
      }
      applyCommittedChange(next, message, focusNodeId, options?.onApplied);
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "操作失败";
      setInteractionError({ dayId: day.id, message: errorMessage });
      onAnnounce(errorMessage);
      return false;
    }
  };

  const orderedNodes = [...day.nodes].sort((left, right) => {
    const slots: TimeSlot[] = ["morning", "afternoon", "evening"];
    return slots.indexOf(left.timeSlot) - slots.indexOf(right.timeSlot) || left.sortOrder - right.sortOrder;
  });
  const explicitProfessionalRoutes = hasExplicitProfessionalRoutes(day, editor.segments);
  const displayedSegments = railwayDaySegments(editor);
  const dayRoutes = displayedSegments.filter((segment) => segment.startDayId === day.id && segment.endDayId === day.id);
  const parallelNodeColumns = new Map<string, number>();
  const columnPaths = day.parallelTracks?.tracks.map((track) => track.nodeIds)
    ?? [];
  if (explicitProfessionalRoutes) {
    dayRoutes.forEach((segment) => columnPaths.push([segment.fromNodeId, segment.toNodeId]));
  }
  // 共享汇合点必须排在所有前驱之后；按阶段成列，不让较短分支提前汇合。
  for (let pass = 0; pass < orderedNodes.length; pass += 1) {
    let changed = false;
    columnPaths.forEach((nodeIds) => {
      nodeIds.forEach((nodeId, index) => {
        const column = index === 0 ? 1 : (parallelNodeColumns.get(nodeIds[index - 1]) ?? 1) + 1;
        if (column > (parallelNodeColumns.get(nodeId) ?? 0)) {
          parallelNodeColumns.set(nodeId, column);
          changed = true;
        }
      });
    });
    if (!changed) break;
  }
  const nodeLocations = new Map(editor.days.flatMap((item) => item.nodes.map((node) => [node.id, {
    dayId: item.id,
    dayNumber: item.dayNumber,
    node,
  }] as const)));
  const incompleteSegments = editor.segments.filter((segment) => {
    const fromLocation = nodeLocations.get(segment.fromNodeId);
    const toLocation = nodeLocations.get(segment.toNodeId);
    if (Boolean(fromLocation) === Boolean(toLocation)) return false;
    return (fromLocation ?? toLocation)?.dayId === day.id;
  });

  const segmentsAfter = (nodeId: string) => editor.segments.filter((segment) => segment.fromNodeId === nodeId);

  /** 仅合并同端点且交通事实一致的专业路线，避免共享出发被重复绘制。 */
  const groupDisplayedSegments = (segments: ItinerarySegment[]) => {
    const groups = new Map<string, { segment: ItinerarySegment; majorIds: string[] }>();
    segments.forEach((segment) => {
      const selected = segment.sourcePayload?.selected;
      const key = JSON.stringify([
        segment.fromNodeId,
        segment.toNodeId,
        segment.transportMode,
        selected?.provider ?? null,
        selected?.optionId ?? null,
        selected?.routeCode ?? segment.routeCode ?? null,
        selected?.departureAt ?? segment.departureAt ?? null,
        selected?.arrivalAt ?? segment.arrivalAt ?? null,
        selected?.distanceKm ?? segment.distanceKm ?? null,
        selected?.durationMinutes ?? segment.durationMinutes ?? null,
      ]);
      const current = groups.get(key);
      if (current) {
        if (segment.majorId && !current.majorIds.includes(segment.majorId)) current.majorIds.push(segment.majorId);
      } else {
        groups.set(key, { segment, majorIds: segment.majorId ? [segment.majorId] : [] });
      }
    });
    return [...groups.values()];
  };
  const openTransport = (segment: ItinerarySegment, trigger: EventTarget | null) => {
    segment = editor.segments.find((item) => item.id === segment.id) ?? segment;
    if (trigger instanceof HTMLElement) transportTriggerRef.current = trigger;
    restoreTransportFocusRef.current = false;
    onModalDraftChange?.(false);
    try {
      setTransportBaseline(captureTransportWorkspaceBaseline(editor, segment));
      setTransportSegment(segment);
      try {
        setRailwayTransferContext(resolveRailwayTransferContext(editor, segment.id));
      } catch (reason) {
        setRailwayTransferContext(null);
        const message = reason instanceof Error ? reason.message : "原中转链已改变，请逐段编辑";
        setInteractionError({ dayId: day.id, message });
        onAnnounce(message);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "无法打开交通工作区";
      setInteractionError({ dayId: day.id, message });
      onAnnounce(message);
    }
  };
  const closeTransport = (restoreFocus = true) => {
    restoreTransportFocusRef.current = restoreFocus;
    onModalDraftChange?.(false);
    setTransportSegment(null);
    setTransportBaseline(null);
    setRailwayTransferContext(null);
    if (restoreFocus) queueMicrotask(restoreTransportFocus);
  };
  const restoreTransportFocus = () => {
    if (!restoreTransportFocusRef.current) return;
    restoreTransportFocusRef.current = false;
    transportTriggerRef.current?.focus();
  };
  const openDeleteDialog = (node: ItineraryNode, trigger: EventTarget | null) => {
    if (trigger instanceof HTMLElement) deleteDialogTriggerRef.current = trigger;
    restoreDeleteDialogFocusRef.current = false;
    setPendingDeleteNode(node);
  };
  const closeDeleteDialog = (restoreFocus: boolean) => {
    restoreDeleteDialogFocusRef.current = restoreFocus;
    setPendingDeleteNode(null);
  };
  const restoreDeleteDialogFocus = () => {
    if (!restoreDeleteDialogFocusRef.current) return;
    restoreDeleteDialogFocusRef.current = false;
    deleteDialogTriggerRef.current?.focus();
  };
  const pendingDeleteIsLegacySnapshot = Boolean(
    pendingDeleteNode
    && pendingDeleteNode.pointSpuId == null
    && pendingDeleteNode.stayAreaId == null
    && pendingDeleteNode.organizationSiteId == null,
  );
  const pointDialogTitle = pointDialog?.mode === "edit"
    ? "编辑点位"
    : pointDialog?.mode === "before"
      ? `在“${pointDialog.node.title}”前插入点位`
      : pointDialog?.mode === "after"
        ? `在“${pointDialog.node.title}”后插入点位`
        : "添加点位";
  const openPointDialog = (next: PointDialogState, trigger: EventTarget | null) => {
    if (trigger instanceof HTMLElement) pointDialogTriggerRef.current = trigger;
    restorePointDialogFocusRef.current = false;
    setInteractionError(null);
    onModalDraftChange?.(false);
    setPointDialog(next);
  };
  const closePointDialog = () => {
    restorePointDialogFocusRef.current = true;
    onModalDraftChange?.(false);
    setPointDialog(null);
    queueMicrotask(() => {
      if (!restorePointDialogFocusRef.current) return;
      restorePointDialogFocusRef.current = false;
      pointDialogTriggerRef.current?.focus();
    });
  };

  const moveWithinSlot = (node: ItineraryNode, direction: -1 | 1) => {
    const slotNodes = orderedNodes.filter((item) => item.timeSlot === node.timeSlot);
    const index = slotNodes.findIndex((item) => item.id === node.id);
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= slotNodes.length) return;
    commit(
      () => movePoint(editor, node.id, { dayId: day.id, timeSlot: node.timeSlot, index: targetIndex }, createId),
      `${node.title}已${direction < 0 ? "上移" : "下移"}`,
      node.id,
      { confirmTransportLoss: true },
    );
  };

  const moveToSlot = (node: ItineraryNode, timeSlot: TimeSlot, keepTerminal = false) => {
    const exemptSegmentIds = railwaySegmentsToRequeryAcrossSlot(editor, node, timeSlot);
    const targetIndex = keepTerminal
      ? appendIndex(editor, day.id, timeSlot, node.id)
      : editor.days.find((item) => item.id === day.id)?.nodes
        .filter((item) => item.id !== node.id && item.timeSlot === timeSlot).length ?? 0;
    commit(
      () => movePoint(editor, node.id, { dayId: day.id, timeSlot, index: targetIndex }, createId),
      `${node.title}已移动到${slotLabels[timeSlot]}`,
      node.id,
      { confirmTransportLoss: true, exemptTransportSegmentIds: exemptSegmentIds },
    );
  };

  const moveBeforeNode = (sourceId: string, target: ItineraryNode) => {
    if (!sourceId || sourceId === target.id) return;
    const source = orderedNodes.find((item) => item.id === sourceId);
    if (!source) return;
    const targetSlotNodes = orderedNodes.filter((item) => item.timeSlot === target.timeSlot);
    const targetIndex = targetSlotNodes.findIndex((item) => item.id === target.id);
    const sourceIndex = targetSlotNodes.findIndex((item) => item.id === source.id);
    const insertionIndex = source.timeSlot === target.timeSlot && sourceIndex < targetIndex
      ? targetIndex - 1
      : targetIndex;
    const exemptSegmentIds = railwaySegmentsToRequeryAcrossSlot(editor, source, target.timeSlot);
    commit(
      () => movePoint(
        editor,
        source.id,
        { dayId: day.id, timeSlot: target.timeSlot, index: insertionIndex },
        createId,
      ),
      `${source.title}已移动到${target.title}之前`,
      source.id,
      { confirmTransportLoss: true, exemptTransportSegmentIds: exemptSegmentIds },
    );
  };

  const clearDragFeedback = () => {
    setDraggedNodeId("");
    setDragTarget(null);
  };

  /** 按当前指针命中元素同步节点插入线或目标时段反馈。 */
  const updatePointerDragTarget = (clientX: number, clientY: number) => {
    const pointerDrag = pointerDragRef.current;
    if (!pointerDrag) return;
    pointerDrag.lastX = clientX;
    pointerDrag.lastY = clientY;
    if (Math.hypot(clientX - pointerDrag.startX, clientY - pointerDrag.startY) < 8) return;
    const target = document.elementFromPoint?.(clientX, clientY);
    if (!(target instanceof Element)) {
      setDragTarget(null);
      return;
    }
    const targetNodeId = target.closest<HTMLElement>("[data-itinerary-node-id]")
      ?.dataset.itineraryNodeId;
    if (targetNodeId && targetNodeId !== pointerDrag.nodeId) {
      setDragTarget({ kind: "node", nodeId: targetNodeId });
      return;
    }
    const timeSlot = target.closest<HTMLElement>("[data-itinerary-time-slot]")
      ?.dataset.itineraryTimeSlot as TimeSlot | undefined;
    setDragTarget(timeSlot ? { kind: "slot", timeSlot } : null);
  };

  const beginPointerDrag = (
    event: React.PointerEvent<HTMLElement>,
    node: ItineraryNode,
  ) => {
    if (event.button !== 0 || event.isPrimary === false || pointerDragRef.current) return;
    const startedOnDragHandle = event.target instanceof Element
      && Boolean(event.target.closest(".itinerary-drag-handle"));
    if ((event.pointerType === "touch" || event.pointerType === "pen") && !startedOnDragHandle) return;
    if (
      event.target instanceof Element
      && event.target.closest("button,input,select,textarea,a")
      && !startedOnDragHandle
    ) return;
    pointerDragRef.current = {
      nodeId: node.id,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      lastX: event.clientX,
      lastY: event.clientY,
      pointerType: event.pointerType,
      nativeDragging: false,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setDraggedNodeId(node.id);
    setDragTarget(null);
  };

  const finishPointerMove = (clientX: number, clientY: number) => {
    const pointerDrag = pointerDragRef.current;
    if (!pointerDrag) return;
    pointerDragRef.current = null;
    clearDragFeedback();
    const missingNativeEndCoordinates = pointerDrag.nativeDragging && clientX === 0 && clientY === 0;
    const resolvedX = missingNativeEndCoordinates ? pointerDrag.lastX : clientX;
    const resolvedY = missingNativeEndCoordinates ? pointerDrag.lastY : clientY;
    if (Math.hypot(
      resolvedX - pointerDrag.startX,
      resolvedY - pointerDrag.startY,
    ) < 8) return;
    const target = document.elementFromPoint?.(resolvedX, resolvedY);
    if (!(target instanceof Element)) return;
    const targetNodeId = target.closest<HTMLElement>("[data-itinerary-node-id]")
      ?.dataset.itineraryNodeId;
    if (targetNodeId) {
      const targetNode = orderedNodes.find((item) => item.id === targetNodeId);
      if (targetNode) moveBeforeNode(pointerDrag.nodeId, targetNode);
      return;
    }
    const timeSlot = target.closest<HTMLElement>("[data-itinerary-time-slot]")
      ?.dataset.itineraryTimeSlot as TimeSlot | undefined;
    const source = orderedNodes.find((item) => item.id === pointerDrag.nodeId);
    if (source && timeSlot) moveToSlot(source, timeSlot, true);
  };

  const finishPointerDrag = (event: React.PointerEvent<HTMLElement>) => {
    const pointerDrag = pointerDragRef.current;
    if (!pointerDrag || pointerDrag.pointerId !== event.pointerId) return;
    finishPointerMove(event.clientX, event.clientY);
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  const beginSlotDragTarget = (event: React.DragEvent<HTMLElement>, timeSlot: TimeSlot) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    setDragTarget({ kind: "slot", timeSlot });
  };

  const leaveSlotDragTarget = (event: React.DragEvent<HTMLElement>, timeSlot: TimeSlot) => {
    if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) return;
    setDragTarget((current) => current?.kind === "slot" && current.timeSlot === timeSlot ? null : current);
  };

  const dropIntoSlot = (event: React.DragEvent<HTMLElement>, timeSlot: TimeSlot) => {
    event.preventDefault();
    const sourceId = event.dataTransfer.getData("text/plain") || draggedNodeId;
    const source = orderedNodes.find((item) => item.id === sourceId);
    pointerDragRef.current = null;
    clearDragFeedback();
    if (source) moveToSlot(source, timeSlot, true);
  };

  const getAllowedPointKinds = useCallback((timeSlot: TimeSlot, majorId?: string | null) => {
    if (!pointDialog) return pointNodeCandidateKinds;
    let pointPreviewIssueKeys = nodePositionIssueKeys(editor);
    try {
      let pointSequence = 0;
      pointPreviewIssueKeys = nodePositionIssueKeys(applyPointDraft(
        editor,
        day.id,
        pointDialog,
        candidatePreviewDraft("point", timeSlot),
        () => `point-position-baseline-${pointSequence++}`,
        majorId ?? pointDialogMajorId,
      ));
    } catch { /* 开放分支可能只允许补公共端点，其他类型继续分别预演。 */ }
    return pointNodeCandidateKinds.filter((kind) => {
      let sequence = 0;
      try {
        const preview = applyPointDraft(
          editor,
          day.id,
          pointDialog,
          candidatePreviewDraft(kind, timeSlot),
          () => `point-position-preview-${sequence++}`,
          majorId ?? pointDialogMajorId,
        );
        return !hasNewNodePositionIssues(pointPreviewIssueKeys, nodePositionIssueKeys(preview));
      } catch {
        return false;
      }
    });
  }, [day.id, editor, pointDialog, pointDialogMajorId]);

  const submitPoint = (draft: NodeDraft & { timeSlot: TimeSlot; majorId?: string | null }) => {
    if (!pointDialog) return;
    const target = pointDialog;
    const { majorId, ...nodeDraft } = draft;
    const action = target.mode === "edit" ? "已更新" : target.mode === "add" ? "已添加" : "已插入";
    if (commit(
      () => applyPointDraft(editor, day.id, target, nodeDraft, createId, majorId),
      `${draft.title}${action}${majorId ? `（${professionalMajorOptions.find((major) => major.id === majorId)?.name ?? "专业"}）` : ""}`,
      target.mode === "edit" ? target.node.id : undefined,
      {
        confirmTransportLoss: true,
        transportLossAction: target.mode === "edit" ? "修改" : target.mode === "add" ? "添加" : "插入",
        onApplied: () => {
          onModalDraftChange?.(false);
          setPointDialog(null);
        },
      },
    )) {
      onModalDraftChange?.(false);
      setPointDialog(null);
    }
  };

  const submitRailway = (draft: RailwayInsertionDraft) => {
    if (!pointDialog || pointDialog.mode === "edit") return;
    const target = pointDialog;
    const placement = target.mode;
    const action = placement === "add" ? "添加" : placement === "before" ? "前插" : "后插";
    commit(
      () => insertSelectedRailway(editor, target.mode === "add" ? day.id : target.node.id, placement, draft, createId),
      `${draft.origin.title} → ${draft.destination.title}已${action}`,
      undefined,
      {
        confirmTransportLoss: true,
        transportLossAction: action,
        onApplied: () => {
          onModalDraftChange?.(false);
          setPointDialog(null);
        },
      },
    );
  };

  const routePlacement = (node: ItineraryNode, destination: ItineraryNode): "inline" | "cross-slot" | "cross-day" => {
    const destinationLocation = nodeLocations.get(destination.id);
    if (!destinationLocation || destinationLocation.dayId !== day.id) return "cross-day";
    if (day.isFullDay || destination.timeSlot === node.timeSlot) return "inline";
    return "cross-slot";
  };

  const renderRouteActions = (segment: ItinerarySegment, destination: ItineraryNode | null, displayedMajorIds?: string[]) => {
    // 编辑器骨架必须保留交通方式文字；住宿端点的隐藏规则只属于客户预览输出。
    const summary = formatSegmentRouteSummary(segment, { hideShortBusDetails });
    const placementNotice = transportPlacementNotice(segment, nodeLocations.get(segment.fromNodeId)?.node, destination, editor.segments);
    const placementError = transportPlacementError(segment, nodeLocations.get(segment.fromNodeId)?.node, destination, editor.segments);
    const summaryId = `itinerary-route-summary-${segment.id}`;
    const noticeId = `itinerary-route-notice-${segment.id}`;
    const noticeLabel = segment.startDayId !== segment.endDayId || segment.departureAt?.slice(0, 10) !== segment.arrivalAt?.slice(0, 10)
      ? "跨日到达，待核对" : "时段不一致";
    const requiresConfirmation = requiresRouteConfirmation(segment);
    const sharedRoute = (displayedMajorIds?.length ?? 0) > 1;
    return <div key={segment.id} className={`itinerary-route-actions${segment.transportMode !== "walk" ? " has-query" : ""}`} data-major-id={sharedRoute ? undefined : segment.majorId ?? undefined}
      data-itinerary-segment-id={segment.id}
      data-transport-mode={segment.transportMode}
      style={segment.majorId && !sharedRoute ? { "--itinerary-route-color": getProfessionalColor(segment.majorId, colorMajorIds) } as CSSProperties : undefined}>
      <div className="itinerary-route-connector-content">
        <Tooltip content={[summary, placementNotice].filter(Boolean).join("；")} trigger={["hover", "focus"]}>
          <button
            type="button"
            className={`itinerary-route-connector${requiresConfirmation ? " is-pending" : ""}${professionalSettings ? " is-professional" : ""}`}
            aria-label={`编辑${nodeLocations.get(segment.fromNodeId)?.node.title ?? "起点"}到${destination?.title ?? "终点"}的路线`}
            aria-describedby={placementNotice ? `${summaryId} ${noticeId}` : summaryId}
            aria-busy={calculatingSegmentIds?.has(segment.id) || undefined}
            onClick={(event) => openTransport(segment, event.currentTarget)}
          >
            <span className="itinerary-route-connector-line" aria-hidden="true"><ArrowRight size={15} /></span>
            <ItineraryRouteSummaryCard segment={segment} descriptionId={summaryId} hideShortBusDetails={hideShortBusDetails} calculating={calculatingSegmentIds?.has(segment.id)} />
            {placementNotice ? <small className={`itinerary-route-placement-notice${placementError ? " is-error" : ""}`} role={placementError ? "alert" : "status"}>
              <TriangleAlert size={13} aria-hidden="true" />
              <span aria-hidden="true">{noticeLabel}</span>
              <span id={noticeId} className="sr-only">{placementNotice}</span>
            </small> : null}
          </button>
        </Tooltip>
        {segment.transportMode !== "walk" ? (
          <Button
            className="itinerary-route-query"
            icon={<Search size={16} />}
            aria-label="查询方案"
            title={segment.transportMode === "bus" ? "大巴测算" : "查询方案"}
            onClick={(event) => openTransport(segment, event.currentTarget)}
          />
        ) : null}
      </div>
    </div>;
  };

  const renderNode = (node: ItineraryNode) => {
    const slotNodes = orderedNodes.filter((item) => item.timeSlot === node.timeSlot);
    const slotIndex = slotNodes.findIndex((item) => item.id === node.id);
    const beforeBlockedReason = orderedNodes.length > 1
      && orderedNodes[0]?.id === node.id
      && endpointNodeTypes.has(node.nodeType)
      ? "学校或住宿已是当天首点，不能在其前插入点位"
      : undefined;
    const afterBlockedReason = orderedNodes.length > 1
      && orderedNodes.at(-1)?.id === node.id
      && endpointNodeTypes.has(node.nodeType)
      ? "学校或住宿已是当天末点，不能在其后插入点位"
      : undefined;
    const nodeMajorId = getProfessionalNodeMajorId(editor, day.id, node.id)
      ?? (!hasParallelTracks && !endpointNodeTypes.has(node.nodeType) ? editableMainProfessionalMajorId : null);
    const nodeGridColumn = hasParallelTracks
      ? (parallelNodeColumns.get(node.id) ?? Math.max(1, orderedNodes.findIndex((item) => item.id === node.id) + 1))
      : null;
    return (
      <li
        key={node.id}
        className={`itinerary-node-row${draggedNodeId === node.id ? " is-dragging" : ""}${dragTarget?.kind === "node" && dragTarget.nodeId === node.id ? " is-drop-target" : ""}`}
        aria-label={node.title}
        data-itinerary-node-id={node.id}
        data-track-column={nodeGridColumn ?? undefined}
        onDragEnter={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setDragTarget(draggedNodeId && draggedNodeId !== node.id
            ? { kind: "node", nodeId: node.id }
            : null);
        }}
        onDragOver={(event) => {
          event.preventDefault();
          event.stopPropagation();
          event.dataTransfer.dropEffect = "move";
          setDragTarget(draggedNodeId && draggedNodeId !== node.id
            ? { kind: "node", nodeId: node.id }
            : null);
        }}
        onDragLeave={(event) => {
          if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) return;
          setDragTarget((current) => current?.kind === "node" && current.nodeId === node.id ? null : current);
        }}
        onDrop={(event) => {
          event.preventDefault();
          event.stopPropagation();
          const sourceId = event.dataTransfer.getData("text/plain") || draggedNodeId;
          pointerDragRef.current = null;
          clearDragFeedback();
          moveBeforeNode(sourceId, node);
        }}
      >
        <article
          className="itinerary-node-card"
          data-major-id={nodeMajorId ?? undefined}
          style={nodeMajorId ? { borderLeft: `4px solid ${getProfessionalColor(nodeMajorId, colorMajorIds)}` } : undefined}
          draggable
          onPointerDown={(event) => beginPointerDrag(event, node)}
          onPointerMove={(event) => {
            if (pointerDragRef.current?.pointerId !== event.pointerId) return;
            updatePointerDragTarget(event.clientX, event.clientY);
          }}
          onPointerUp={finishPointerDrag}
          onPointerCancel={(event) => {
            const pointerDrag = pointerDragRef.current;
            if (!pointerDrag || pointerDrag.pointerId !== event.pointerId || pointerDrag.nativeDragging) return;
            pointerDragRef.current = null;
            clearDragFeedback();
          }}
          onLostPointerCapture={(event) => {
            const pointerDrag = pointerDragRef.current;
            if (!pointerDrag || pointerDrag.pointerId !== event.pointerId || pointerDrag.nativeDragging) return;
            pointerDragRef.current = null;
            clearDragFeedback();
          }}
          onDragStart={(event) => {
            const pointerDrag = pointerDragRef.current;
            if (pointerDrag?.nodeId === node.id
              && (pointerDrag.pointerType === "touch" || pointerDrag.pointerType === "pen")) {
              event.preventDefault();
              return;
            }
            if (
              event.target instanceof Element
              && event.target.closest("button,input,select,textarea,a")
              && !event.target.closest(".itinerary-drag-handle")
            ) {
              event.preventDefault();
              return;
            }
            if (pointerDragRef.current?.nodeId === node.id) {
              pointerDragRef.current.nativeDragging = true;
            }
            setDraggedNodeId(node.id);
            setDragTarget(null);
            event.dataTransfer.effectAllowed = "move";
            event.dataTransfer.setData("text/plain", node.id);
          }}
          onDrag={(event) => {
            if (pointerDragRef.current?.nodeId === node.id && (event.clientX || event.clientY)) {
              pointerDragRef.current.lastX = event.clientX;
              pointerDragRef.current.lastY = event.clientY;
            }
          }}
          onDragEnd={(event) => {
            if (pointerDragRef.current) finishPointerMove(event.clientX, event.clientY);
            else clearDragFeedback();
          }}
        >
          <button
            id={`itinerary-node-handle-${node.id}`}
            className="itinerary-icon-button itinerary-drag-handle"
            type="button"
            style={typeof window !== "undefined" && window.matchMedia?.("(any-pointer: coarse)").matches
              ? { display: "inline-grid", width: 44, height: 44 }
              : undefined}
            aria-label={`拖动${node.title}`}
            title="拖动排序"
          >
            <GripVertical size={17} />
          </button>
          <div className="itinerary-node-copy">
            <span>{nodeTypeLabels[node.nodeType]}</span>
            <strong>{node.title}</strong>
            {node.description ? <span className="sr-only">{node.description}</span> : null}
          </div>
          <div className="itinerary-node-controls" aria-label={`${node.title}排序和编辑操作`}>
            <button
              className="itinerary-icon-button itinerary-node-reorder-control"
              type="button"
              aria-label={`上移${node.title}`}
              title="上移"
              disabled={slotIndex === 0}
              onClick={() => moveWithinSlot(node, -1)}
            >
              <ArrowUp size={16} />
            </button>
            <button
              className="itinerary-icon-button itinerary-node-reorder-control"
              type="button"
              aria-label={`下移${node.title}`}
              title="下移"
              disabled={slotIndex === slotNodes.length - 1}
              onClick={() => moveWithinSlot(node, 1)}
            >
              <ArrowDown size={16} />
            </button>
            <label className="itinerary-inline-select itinerary-node-reorder-control">
              <span className="sr-only">移动{node.title}到时段</span>
              <select
                value={node.timeSlot}
                onChange={(event) => moveToSlot(node, event.target.value as TimeSlot, true)}
              >
                <option value="morning">上午</option>
                <option value="afternoon">下午</option>
                <option value="evening">晚上</option>
              </select>
            </label>
            <button
              className="itinerary-icon-button"
              type="button"
              aria-label={`编辑${node.title}`}
              title="编辑点位"
              onClick={(event) => openPointDialog(
                { mode: "edit", node, slot: node.timeSlot },
                event.currentTarget,
              )}
            >
              <Pencil size={16} />
            </button>
            <button
              className="itinerary-icon-button itinerary-icon-button--danger"
              type="button"
              aria-label={`删除${node.title}`}
              title="删除点位"
              onClick={(event) => openDeleteDialog(node, event.currentTarget)}
            >
              <Trash2 size={16} />
            </button>
          </div>
        </article>
        <div className="itinerary-insert-actions">
          <Button size="mini" icon={<Plus size={14} />} disabled={Boolean(beforeBlockedReason)} title={beforeBlockedReason} onClick={(event) => openPointDialog(
            { mode: "before", node, slot: node.timeSlot },
            event.currentTarget,
          )}>
            前插
          </Button>
          <Button size="mini" icon={<Plus size={14} />} disabled={Boolean(afterBlockedReason)} title={afterBlockedReason} onClick={(event) => openPointDialog(
            { mode: "after", node, slot: node.timeSlot },
            event.currentTarget,
          )}>
            后插
          </Button>
        </div>
      </li>
    );
  };

  const renderIncompleteRoutes = () => incompleteSegments.map((segment) => {
    const fromLocation = nodeLocations.get(segment.fromNodeId);
    const toLocation = nodeLocations.get(segment.toNodeId);
    return (
      <div key={segment.id} className="itinerary-slot-bridge" data-route-placement="incomplete">
        <span className="itinerary-slot-bridge-label">{fromLocation ? "终点待补" : "起点待补"}</span>
        {renderRouteActions(segment, toLocation?.node ?? null)}
      </div>
    );
  });

  const explicitRouteGroups = new Map<string | null, ItinerarySegment[]>();
  if (explicitProfessionalRoutes) {
    dayRoutes.forEach((segment) => {
      const group = explicitRouteGroups.get(segment.majorId ?? null) ?? [];
      group.push(segment);
      explicitRouteGroups.set(segment.majorId ?? null, group);
    });
  }
  const routeOverviewItems = hasParallelTracks
    ? day.parallelTracks!.tracks.map((track) => ({
      majorId: track.majorId ?? null,
      path: track.nodeIds.map((id) => nodeLocations.get(id)?.node.title ?? "待补点位").join(" → "),
    }))
    : explicitProfessionalRoutes
    ? [...explicitRouteGroups.entries()].map(([majorId, segments]) => ({
      majorId,
      path: describeSegmentPaths(segments, nodeLocations),
    }))
    : [{
      majorId: null,
      path: orderedNodes.length === 1
        ? orderedNodes[0].title
        : orderedNodes.length > 1
          ? orderedNodes.map((node) => node.title).join(" → ")
          : "待添加点位",
    }];
  const routePath = routeOverviewItems
    .map(({ majorId, path }) => `${majorId ? `${majorName(majorId)}：` : ""}${path}`)
    .join("；");
  const routeCount = displayedSegments.filter(
    (segment) => {
      if (segment.startDayId !== day.id) return false;
      const from = nodeLocations.get(segment.fromNodeId)?.node;
      const to = nodeLocations.get(segment.toNodeId)?.node;
      const isDisplayOnlyContinuation = segment.startDayId !== segment.endDayId
        && isStayPointConnection(from?.nodeType, to?.nodeType)
        && !hasSavedTransportFacts(segment);
      return !isDisplayOnlyContinuation;
    },
  ).length;
  const routeStatus = orderedNodes.length ? `${orderedNodes.length} 点 / ${explicitProfessionalRoutes ? "" : "自动 "}${routeCount} 线` : "等待搭骨架";
  const renderEmptySlot = (slot: TimeSlot, label: string) => (
    <div className="itinerary-slot-empty">
      <span className="itinerary-slot-empty-message--drag">把点位拖到这里，系统会按顺序自动连线</span>
      <span className="itinerary-slot-empty-message--touch">本时段暂无点位，可通过“放一个点”补充安排</span>
      <Button
        type="text"
        size="small"
        icon={<Plus size={14} />}
        aria-label={`添加${label}点位`}
        onClick={(event) => openPointDialog({ mode: "add", slot }, event.currentTarget)}
      >
        放一个点
      </Button>
    </div>
  );
  const dayHeader = (
    <header className="itinerary-day-heading">
      <div className="itinerary-day-title-row">
        <div>
          {day.dayNumber === 1 ? (
            <label className="itinerary-day-heading-date" htmlFor="itinerary-start-date">
              <span>DAY 1 ·</span>
              <span className="sr-only">行程开始日期</span>
              <input
                id="itinerary-start-date"
                type="date"
                aria-label="行程开始日期"
                value={editor.startDate ?? ""}
                onChange={(event) => {
                  const value = event.target.value;
                  onChange(setItineraryStartDate(editor, value));
                  onAnnounce(value ? `行程开始日期已更新为${value}` : "行程开始日期已清空");
                }}
              />
            </label>
          ) : <span>DAY {day.dayNumber} · {day.dayDate || "日期待定"}</span>}
          <h2>{day.dayTitle?.trim() || "待定"}</h2>
          <p>{explicitProfessionalRoutes ? "按已保存的专业路线连接；调整点位显示位置不会串联不同专业。" : "先放点、定顺序；交通线会在相邻点之间自动生成。"}</p>
        </div>
        <div className="itinerary-day-actions">
          <Checkbox checked={hideShortBusDetails} onChange={(checked) => {
            setHideShortBusDetails(checked);
            try {
              globalThis.sessionStorage?.setItem("itinerary:hide-short-bus-details", String(checked));
            } catch {
              // 存储不可用时仍保留当前编辑会话的显示选择。
            }
          }}>
            隐藏 1 小时内车程时间和距离
          </Checkbox>
          <Checkbox
            checked={day.isFullDay}
            onChange={(checked) => {
              if (!checked && day.fullDaySummary != null) {
                setConfirmDividedDayId(day.id);
                return;
              }
              onChange({
                ...editor,
                days: editor.days.map((item) => (item.id === day.id ? { ...item, isFullDay: checked } : item)),
              });
            }}
          >
            全天安排
          </Checkbox>
          <>
              <ItineraryProfessionalSettings
                editor={editor}
                onChange={onChange}
                onAnnounce={onAnnounce}
                listMajorCandidates={listMajorCandidates}
                onDraftStateChange={onModalDraftChange}
              />
            <Button size="small" icon={<MapPinPlus size={15} />} aria-label="补全当前日住宿点" onClick={() => commit(
              () => {
                const completed = completeDayStayEndpoints(editor, day.id, createId);
                if (completed === editor) throw new Error("没有找到可复用的相邻日住宿点，或当天住宿起终点已完整。");
                return completed;
              },
              "已参考前后两天的住宿，补上今天的出发地和住宿。请确认是否符合实际安排。",
              undefined,
              { confirmTransportLoss: true, transportLossAction: "补全住宿点" },
            )}>
              补全住宿点
            </Button>
            <Button size="small" icon={<MapPinPlus size={15} />} onClick={(event) => openPointDialog(
              { mode: "add", slot: "morning" },
              event.currentTarget,
            )}>
              添加点位
            </Button>
          </>
        </div>
      </div>
      {displayedProfessionalMajorOptions.length > 0 ? (
        <div className="itinerary-day-legend itinerary-professional-legend" aria-label="专业颜色说明">
          <span className="itinerary-professional-legend-label">专业颜色</span>
          {colorMajorIds.map((majorId) => <span key={majorId}><i style={{ background: getProfessionalColor(majorId, colorMajorIds) }} />{majorName(majorId)}{majorId === displayedMainProfessionalMajorId ? "（主）" : ""}</span>)}
        </div>
      ) : null}
      <div className="itinerary-route-overview">
        <span>本日总路线</span>
        <strong className="itinerary-route-overview-lines" title={routePath}>
          {routeOverviewItems.map(({ majorId, path }, index) => {
            const isProfessional = Boolean(majorId);
            const routeColor = isProfessional
              ? getProfessionalColor(majorId as string, colorMajorIds)
              : undefined;
            return (
              <span
                key={`${majorId ?? "shared"}-${index}`}
                className={`itinerary-route-overview-line${isProfessional ? " is-professional" : ""}`}
                data-major-id={majorId ?? undefined}
                style={routeColor ? { "--itinerary-route-color": routeColor } as CSSProperties : undefined}
              >
                {isProfessional ? <span className="itinerary-route-overview-major">{majorName(majorId)}</span> : null}
                {isProfessional ? <span className="itinerary-route-overview-separator">：</span> : null}
                <span className="itinerary-route-overview-path">{path}</span>
              </span>
            );
          })}
        </strong>
        <span className="itinerary-route-overview-status">{routeStatus}</span>
      </div>
    </header>
  );
  const dayFooter = (
    <footer className="itinerary-day-footer">
      <span className="itinerary-day-continuity">
        {day.stayAreaName?.trim() === "无" && !day.nodes.some((node) => node.nodeType === "stay_area")
          ? "住宿：无（不安排住宿）" : `住宿安排：${orderedNodes.length ? "请确认今天从哪里出发、最后住在哪里" : "添加地点后再确认"}`}
      </span>
      <div className="itinerary-day-legend" aria-label="日程图例">
        <span><i className="is-point" />点位</span>
        <span><i className="is-route" />交通连接</span>
        <span><i className="is-pending" />待确认</span>
      </div>
      <span>{orderedNodes.length} 个点位 · {routeCount} 条路线</span>
    </footer>
  );
  const interactionErrorAlert = interactionError?.dayId === day.id ? (
    <div className="itinerary-interaction-error" role="alert">{interactionError.message}</div>
  ) : null;
  return (
    <section className="itinerary-skeleton-panel itinerary-day-card" aria-label={`第${day.dayNumber}天行程骨架`}>
      {dayHeader}
      {interactionErrorAlert}

      <div className="itinerary-day-body">
        <ItineraryFlowCanvas
          key={day.id}
          day={day}
          points={orderedNodes}
          columns={parallelNodeColumns}
          routes={groupDisplayedSegments(displayedSegments.filter((segment) => (
            nodeLocations.get(segment.fromNodeId)?.dayId === day.id && nodeLocations.has(segment.toNodeId)
          ))).map(({ segment, majorIds }) => {
            const destination = nodeLocations.get(segment.toNodeId)!;
            const crossDay = destination.dayId !== day.id;
            const source = nodeLocations.get(segment.fromNodeId)!.node;
            const directStayContinuation = crossDay
              && isStayPointConnection(source.nodeType, destination.node.nodeType)
              && !hasSavedTransportFacts(segment);
            return {
              id: segment.id, source: segment.fromNodeId,
              target: crossDay ? `continuation-${segment.toNodeId}` : segment.toNodeId,
              content: directStayContinuation ? null : <div data-route-placement={routePlacement(source, destination.node)}>
                {renderRouteActions(segment, destination.node, majorIds)}
              </div>,
              color: majorIds.length === 1 ? getProfessionalColor(majorIds[0], colorMajorIds) : "#94a3b8",
              walking: segment.transportMode === "walk",
              continuation: crossDay ? { title: destination.node.title, dayNumber: destination.dayNumber } : undefined,
            };
          })}
          renderPoint={renderNode}
          renderSlot={(slot) => {
            const nodes = orderedNodes.filter((node) => day.isFullDay || node.timeSlot === slot);
            const label = day.isFullDay ? "全天" : slotLabels[slot];
            return <section
              className={`itinerary-flow-slot${dragTarget?.kind === "slot" && dragTarget.timeSlot === slot ? " is-drop-target" : ""}`}
              aria-labelledby={`slot-${day.id}-${slot}`}
              data-itinerary-time-slot={slot}
              onDragEnter={(event) => beginSlotDragTarget(event, slot)}
              onDragOver={(event) => beginSlotDragTarget(event, slot)}
              onDragLeave={(event) => leaveSlotDragTarget(event, slot)}
              onDrop={(event) => dropIntoSlot(event, slot)}
            >
              <div className="itinerary-time-slot-label">
                <h3 id={`slot-${day.id}-${slot}`}>{label}</h3>
                <span>{nodes.length ? `${nodes.length} 个点` : "暂空"}</span>
              </div>
              {!nodes.length ? renderEmptySlot(slot, label) : null}
            </section>;
          }}
        />
        {renderIncompleteRoutes()}
      </div>
      {dayFooter}

      <ItineraryPointDialog
        visible={Boolean(pointDialog)}
        title={pointDialogTitle}
        initial={pointDialog?.mode === "edit" ? pointDialog.node : null}
        defaultSlot={pointDialog?.slot ?? "morning"}
        lockSlot={pointDialog?.mode === "before" || pointDialog?.mode === "after"}
        onCancel={closePointDialog}
        onSubmit={submitPoint}
        enableRailwaySearch={pointDialog?.mode === "before" || pointDialog?.mode === "after" || pointDialog?.mode === "add"}
        railwaySearchOnAdd={pointDialog?.mode === "add"}
        onRailwaySubmit={submitRailway}
        getRailwayOptions={getRailwayOptions}
        travelDate={day.dayDate}
        majorOptions={pointDialog?.mode === "edit" && professionalSettings && !pointContextMajorId ? [{ id: "", name: "公共" }] : professionalMajorOptions}
        defaultMajorId={pointDialog?.mode === "edit" && !pointContextMajorId ? null : pointDialogMajorId}
        lockMajor={pointDialog?.mode === "edit" || Boolean(pointContextMajorId)}
        listCandidates={listPointCandidates}
        getRecommendationAnchor={(slot, majorId, kind) => {
          if (!pointDialog) return null;
          try {
            // 与保存、可选类型校验共用纯命令；专业分流可能替换相邻主线点位。
            let sequence = 0;
            const preview = applyPointDraft(editor, day.id, pointDialog, candidatePreviewDraft(kind, slot),
              () => `recommendation-preview-${++sequence}`, majorId ?? pointDialogMajorId);
            const previewDay = preview.days.find((item) => item.id === day.id);
            const existingIds = new Set(day.nodes.map((node) => node.id));
            const targetId = pointDialog.mode === "edit" ? pointDialog.node.id
              : previewDay?.nodes.find((node) => !existingIds.has(node.id))?.id;
            return targetId ? resolvePointRecommendationAnchor(preview, {
              dayId: day.id, timeSlot: slot, majorId: majorId ?? pointDialogMajorId, beforeNodeId: targetId,
            }) : null;
          } catch {
            // 不能确定合法插入路径时保留手工搜索，由已有位置校验解释原因。
            return null;
          }
        }}
        getAllowedKinds={getAllowedPointKinds}
        submitError={pointDialog && interactionError?.dayId === day.id ? interactionError.message : undefined}
        onDraftStateChange={onModalDraftChange}
      />
      <Modal
        visible={confirmDividedDayId === day.id}
        title="切换为分时安排"
        closeIcon={<ItineraryDialogCloseButton label="关闭分时切换确认" />}
        okText="使用原分时稿"
        cancelText="取消"
        onCancel={() => setConfirmDividedDayId(null)}
        onOk={() => {
          setConfirmDividedDayId(null);
          onChange({ ...editor, days: editor.days.map((item) => item.id === day.id ? { ...item, isFullDay: false } : item) });
        }}
      >
        切换后将使用原有上午、下午、晚上正文。当前全天稿会保留，再次开启全天时可继续编辑；不会自动拆分或覆盖原分时稿。
      </Modal>
      <Modal
        visible={Boolean(pendingTransportLoss)}
        maskStyle={{ zIndex: 1100 }}
        wrapStyle={{ zIndex: 1101 }}
        title={`确认${pendingTransportLoss?.action ?? "移动"}点位`}
        closeIcon={<ItineraryDialogCloseButton label={`关闭确认${pendingTransportLoss?.action ?? "移动"}点位`} />}
        okText={`仍要${pendingTransportLoss?.action ?? "移动"}`}
        cancelText="取消"
        okButtonProps={{ status: "danger" }}
        onCancel={() => setPendingTransportLoss(null)}
        onOk={() => {
          const pending = pendingTransportLoss;
          setPendingTransportLoss(null);
          if (pending) {
            if (itineraryEditorSemanticFingerprint(editor) !== pending.baselineFingerprint) {
              const message = "行程内容已更新，请重新操作并确认交通影响。";
              setInteractionError({ dayId: day.id, message });
              onAnnounce(message);
              return;
            }
            applyCommittedChange(pending.next, pending.message, pending.focusNodeId, pending.onApplied);
          }
        }}
      >
        此次{pendingTransportLoss?.action ?? "移动"}会替换 {pendingTransportLoss?.count ?? 0} 条已填写交通路线，并清除其中的班次、时刻、距离或主备方案。确认后会保存到本地草稿。
      </Modal>
      <Modal
        visible={Boolean(pendingDeleteNode)}
        title="删除点位"
        closeIcon={<ItineraryDialogCloseButton label="关闭删除点位" />}
        okText="确认删除"
        cancelText="取消"
        okButtonProps={{ status: "danger" }}
        onCancel={() => closeDeleteDialog(true)}
        afterClose={restoreDeleteDialogFocus}
        onOk={() => {
          if (!pendingDeleteNode) return;
          const node = pendingDeleteNode;
          closeDeleteDialog(false);
          commit(() => deleteNode(editor, node.id, createId), `${node.title}已删除`);
        }}
      >
        {pendingDeleteIsLegacySnapshot ? (
          <Alert
            type="warning"
            showIcon
            content="该点位来自历史行程且已失去主数据关联。确认后会删除该点位及相邻路线；保存后不会自动还原原记录，但仍可重新添加点位并重新录入路线。"
          />
        ) : (
          <>确认删除“{pendingDeleteNode?.title}”及相邻路线吗？</>
        )}
      </Modal>
      <Modal
        visible={Boolean(pendingTransportWorkspace)}
        title="确认更换交通端点"
        closeIcon={<ItineraryDialogCloseButton label="关闭确认更换交通端点" />}
        okText="确认更换"
        cancelText="取消"
        okButtonProps={{ status: "danger" }}
        onCancel={() => {
          restoreTransportFocusRef.current = true;
          setPendingTransportWorkspace(null);
        }}
        afterClose={restoreTransportFocus}
        onOk={() => {
          const pending = pendingTransportWorkspace;
          restoreTransportFocusRef.current = true;
          setPendingTransportWorkspace(null);
          if (!pending) return;
          try {
            onChange(applyTransportWorkspaceChange(editor, pending.change));
            setInteractionError(null);
            onAnnounce("交通端点和路线已更新，正在自动保存");
          } catch (error) {
            const message = error instanceof Error ? error.message : "交通路线更新失败";
            setInteractionError({ dayId: day.id, message });
            onAnnounce(message);
          }
        }}
      >
        更换端点会按新端点重新生成另外 {pendingTransportWorkspace?.count ?? 0} 条相邻路线的交通方式，并清除其中原有的班次、时刻、距离或主备方案。
      </Modal>
      <ItineraryTransportOptionsDrawer
        visible={Boolean(transportSegment)}
        segment={transportSegment}
        railwayTransferContext={railwayTransferContext}
        professionalOptions={professionalSettings && transportSegment ? segmentsAfter(transportSegment.fromNodeId).map((segment) => ({
          segmentId: segment.id,
          name: majorName(segment.majorId ?? (hasParallelTracks ? null : editableMainProfessionalMajorId)),
          color: segment.majorId ? getProfessionalColor(segment.majorId, colorMajorIds) : undefined,
        })) : undefined}
        onProfessionalChange={(segmentId) => {
          const segment = editor.segments.find((item) => item.id === segmentId);
          if (segment) openTransport(segment, null);
        }}
        fromNode={transportSegment ? editor.days.flatMap((item) => item.nodes).find((node) => node.id === transportSegment.fromNodeId) ?? null : null}
        toNode={transportSegment ? editor.days.flatMap((item) => item.nodes).find((node) => node.id === transportSegment.toNodeId) ?? null : null}
        travelDate={transportSegment ? editor.days.find((item) => item.id === transportSegment.startDayId)?.dayDate : null}
        departureDayDate={transportSegment ? editor.days.find((item) => item.id === transportSegment.startDayId)?.dayDate : null}
        arrivalDayDate={transportSegment ? editor.days.find((item) => item.id === transportSegment.endDayId)?.dayDate : null}
        onCancel={() => closeTransport()}
        onDraftStateChange={onModalDraftChange}
        onConfirm={(segment, endpointChanges: TransportEndpointChange[] = [], railwayTransfer?: RailwayTransferPlan) => {
          if (!transportBaseline) {
            const message = "交通工作区基线已失效，请关闭后重新编辑";
            setInteractionError({ dayId: day.id, message });
            onAnnounce(message);
            return;
          }
          const change: TransportWorkspaceChange = {
            ...transportBaseline,
            segment,
            endpointChanges,
            railwayTransfer,
          };
          try {
            const lossCount = countTransportWorkspaceFactLoss(editor, change);
            if (lossCount > 0) {
              setPendingTransportWorkspace({ change, count: lossCount });
              closeTransport(false);
              return;
            }
            onChange(applyTransportWorkspaceChange(editor, change));
            setInteractionError(null);
            onAnnounce("交通方案已确认，正在自动保存");
            closeTransport();
          } catch (error) {
            const message = error instanceof Error ? error.message : "交通路线更新失败";
            setInteractionError({ dayId: day.id, message });
            onAnnounce(message);
          }
        }}
        getRailwayOptions={getRailwayOptions ?? (async () => { throw new Error("铁路查询暂不可用"); })}
        getFlightOptions={getFlightOptions ?? (async () => { throw new Error("航班查询暂不可用"); })}
        getDrivingRoute={getDrivingRoute ?? (async () => { throw new Error("大巴测算暂不可用"); })}
        getWalkingRoute={getWalkingRoute ?? (async () => { throw new Error("步行测算暂不可用"); })}
        listPointCandidates={listPointCandidates}
      />
    </section>
  );
}
